import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Lock, Eye, EyeOff, ArrowRight, AlertCircle } from "lucide-react";

const PREVIEW_COOKIE_NAME = process.env.REACT_APP_PREVIEW_GATE_COOKIE || "amidex_preview_access";
const PREVIEW_PASSWORD = process.env.REACT_APP_PREVIEW_GATE_PASSWORD || "";
const PREVIEW_TTL_DAYS = parseInt(process.env.REACT_APP_PREVIEW_GATE_TTL_DAYS || "7", 10);

// Use the same logo as the header
const LOGO_URL = "/amidex-logo.png";

// Cookie utilities
export const setPreviewCookie = () => {
  const expires = new Date();
  expires.setDate(expires.getDate() + PREVIEW_TTL_DAYS);
  document.cookie = `${PREVIEW_COOKIE_NAME}=true; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

export const hasPreviewCookie = () => {
  return document.cookie.split(";").some((c) => c.trim().startsWith(`${PREVIEW_COOKIE_NAME}=`));
};

export const clearPreviewCookie = () => {
  document.cookie = `${PREVIEW_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

const PreviewGatePage = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Simulate a brief delay for UX
    await new Promise((r) => setTimeout(r, 300));

    if (password === PREVIEW_PASSWORD) {
      setPreviewCookie();
      if (onSuccess) {
        onSuccess();
      } else {
        window.location.reload();
      }
    } else {
      setError("Invalid password. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Private Preview | Amidex</title>
        <meta name="robots" content="noindex,nofollow" />
        <meta name="description" content="This is a private preview environment for internal testing." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4 overflow-hidden relative">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/3 -left-24 w-80 h-80 bg-purple-600/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/3 -right-24 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
          
          {/* Grid pattern */}
          <div 
            className="absolute inset-0 opacity-[0.02]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        <div className="relative z-10 max-w-md w-full">
          {/* Logo/Brand */}
          <div className="text-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="Amidex" 
              className="h-10 md:h-12 w-auto mx-auto brightness-0 invert"
              data-testid="preview-logo"
            />
          </div>

          {/* Main Card */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
            {/* Status Badge */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20">
                <Lock className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-purple-300 text-xs font-medium uppercase tracking-wider">Private Preview</span>
              </div>
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-600/20 to-blue-500/20 border border-purple-500/20 flex items-center justify-center">
                <Lock className="w-8 h-8 text-purple-400" />
              </div>
            </div>

            {/* Content */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-white mb-2 tracking-tight">
                Access Required
              </h1>
              <p className="text-slate-400 text-sm leading-relaxed">
                Enter the preview password to continue.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  placeholder="Enter password"
                  className={`w-full pl-12 pr-12 py-4 bg-white/5 border rounded-xl text-white placeholder:text-slate-500 focus:outline-none transition-all ${
                    error 
                      ? "border-red-500/50 focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20" 
                      : "border-white/10 focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20"
                  }`}
                  data-testid="preview-password-input"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  data-testid="preview-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <span className="text-red-300 text-sm">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || !password}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="preview-submit-btn"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Continue to Preview
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>

            {/* Note */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-center text-slate-500 text-xs">
                For internal testing only. Unauthorized access is prohibited.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-slate-600 text-xs">
              © {new Date().getFullYear()} Amidex. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PreviewGatePage;
